import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, RefreshCw } from 'lucide-react';
import { STATUS_LABEL, formatDay, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import { addDays } from '../bookings/shared.jsx';
import { problem } from '../bookings/util.js';

// Operations > Job Card Time Analyser: where each team's time went. For every
// job started between two days: on route, arrived, work started (the risk
// assessment page 2 photo taken), finished; and the time in each part:
// travel, setting up (arrived to work started), working (work started to
// finished, less pauses). Each team's day adds them up, with the idle time
// between finishing one job and setting off for the next. Times worth a look
// are in amber (LOOK). GET /api/job-times.
const LOOK = { travelMins: 60, setupMins: 30, idleMins: 30 };
const clock = (v) => (v ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' }).format(new Date(v)) : '–');
const hm = (m) => (m == null ? '–' : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`);
const weekday = (d) => new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
// Monday of the week `day` is in.
const monday = (day) => addDays(day, -((new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7));

function Flag({ mins, over, title }) {
  const warn = mins != null && over != null && mins > over;
  return <span className={warn ? 'warn-text strong' : ''} title={warn ? title : undefined}>{hm(mins)}</span>;
}

export default function JobTimes() {
  const today = todayUk();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [teamId, setTeamId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api(`/job-times?from=${from}&to=${to}${teamId ? `&teamId=${teamId}` : ''}`));
    } catch (e) {
      setError(problem(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [from, to, teamId]); // eslint-disable-line react-hooks/exhaustive-deps
  const range = (a, b) => { setFrom(a); setTo(b); };

  // Across everything shown: averages per job, and the idle total.
  const totals = useMemo(() => {
    const jobs = (data?.days || []).flatMap((d) => d.jobs.map((j) => j.timing));
    const avg = (k) => {
      const v = jobs.map((t) => t[k]).filter((x) => x != null);
      return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
    };
    return {
      jobs: jobs.length,
      travel: avg('travelMins'),
      setup: avg('setupMins'),
      work: avg('workMins'),
      idle: (data?.days || []).reduce((n, d) => n + d.summary.idleMins, 0),
      share: (() => {
        const v = (data?.days || []).map((d) => d.summary.workShare).filter((x) => x != null);
        return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
      })(),
    };
  }, [data]);

  const csv = () => {
    const head = ['Day', 'Team', 'Job', 'Address', 'Postcode', 'Outcome', 'On route', 'Arrived', 'Work started', 'Finished', 'Travel (min)', 'At a storeroom (min)', 'Setting up (min)', 'Working (min)', 'Paused (min)', 'Total (min)'];
    const rows = (data?.days || []).flatMap((d) => d.jobs.map((j) => [
      formatDay(d.day), d.teamName, j.reference, j.address, j.postcode, STATUS_LABEL[j.status] || j.status,
      clock(j.timing.onRouteAt), clock(j.timing.arrivedAt), clock(j.timing.workStartedAt), clock(j.timing.finishedAt),
      j.timing.travelMins ?? '', j.timing.storeroomMins || '', j.timing.setupMins ?? '', j.timing.workMins ?? '', j.timing.pausedMins ?? '', j.timing.totalMins ?? '',
    ]));
    const text = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([String.fromCharCode(0xfeff) + text], { type: 'text/csv' }) /* BOM: Excel reads it as UTF-8 */);
    a.download = `Job times ${formatDay(from).split('/').reverse().join('-')}${from === to ? '' : ` to ${formatDay(to).split('/').reverse().join('-')}`}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  };

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Job Card Time Analyser</h1>
            <p className="muted">Where each team’s time goes. Work starts when the risk assessment page 2 photo is taken. Times in amber are worth a look: travel over {LOOK.travelMins} minutes, setting up over {LOOK.setupMins}, or idle between jobs over {LOOK.idleMins}.</p>
          </div>
        </div>
        <div className="filters job-times-filters">
          <label className="fld"><span>From</span><input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
          <label className="fld"><span>To</span><input type="date" value={to} min={from} max={today} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
          <label className="fld">
            <span>Team</span>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              <option value="">All teams</option>
              {(data?.teams || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <div className="job-times-quick">
            <button type="button" className="btn" onClick={() => range(today, today)}>Today</button>
            <button type="button" className="btn" onClick={() => range(addDays(today, -1), addDays(today, -1))}>Yesterday</button>
            <button type="button" className="btn" onClick={() => range(monday(today), today)}>This week</button>
            <button type="button" className="btn" onClick={() => range(addDays(monday(today), -7), addDays(monday(today), -1))}>Last week</button>
          </div>
        </div>
        <div className="survey-tools">
          <button type="button" className="btn primary with-icon" disabled={loading} onClick={load}><RefreshCw size={16} aria-hidden="true" className={loading ? 'spin' : ''} /> Refresh</button>
          <button type="button" className="btn primary with-icon" disabled={!data?.days?.length} onClick={csv}><Download size={16} aria-hidden="true" /> Download CSV</button>
        </div>
        {error && <p className="notice-line notice-bad">{error}</p>}
        {data && data.days.length > 0 && (
          <div className="job-times-sum">
            <div><span className="small muted">Jobs</span><strong>{totals.jobs}</strong></div>
            <div><span className="small muted">Travel a job</span><strong>{hm(totals.travel)}</strong><span className="small muted">average</span></div>
            <div><span className="small muted">Setting up a job</span><strong>{hm(totals.setup)}</strong><span className="small muted">average</span></div>
            <div><span className="small muted">Working a job</span><strong>{hm(totals.work)}</strong><span className="small muted">average</span></div>
            <div><span className="small muted">Idle between jobs</span><strong>{hm(totals.idle)}</strong><span className="small muted">in all</span></div>
            <div><span className="small muted">Day spent working</span><strong>{totals.share == null ? '–' : `${totals.share}%`}</strong><span className="small muted">average</span></div>
          </div>
        )}
      </section>

      {!data && !error && <section className="card"><p className="muted">Loading…</p></section>}
      {data && !data.days.length && <section className="card"><p className="muted">No jobs started {from === to ? `on ${formatDay(from)}` : `from ${formatDay(from)} to ${formatDay(to)}`}.</p></section>}
      {data?.days.map((d) => {
        const s = d.summary;
        return (
          <section key={`${d.day}-${d.teamId}`} className="card">
            <h2 className="job-times-day">{weekday(d.day)} {formatDay(d.day)} · {d.teamName}{d.crew.length ? <span className="muted small"> ({d.crew.join(', ')})</span> : null}</h2>
            <div className="job-times-chips small">
              <span><strong>Day</strong> {clock(s.firstOnRouteAt)} to {clock(s.lastFinishedAt)} ({hm(s.spanMins)})</span>
              <span><strong>Travel</strong> {hm(s.travelMins)}</span>
              <span><strong>Setting up</strong> {hm(s.setupMins)}</span>
              <span><strong>Working</strong> {hm(s.workMins)}</span>
              {s.pausedMins > 0 && <span><strong>Paused</strong> {hm(s.pausedMins)}</span>}
              <span><strong>Idle between jobs</strong> <Flag mins={s.idleMins} over={LOOK.idleMins} title="A long gap between finishing a job and setting off for the next" /></span>
              {s.workShare != null && <span><strong>Working</strong> {s.workShare}% of the day</span>}
            </div>
            <div className="table-scroll">
              <table className="table job-times-table">
                {/* The same widths in every team's table, so the columns line up down the page. */}
                <colgroup>
                  <col className="jt-job" /><col className="jt-outcome" />
                  {Array.from({ length: 8 }, (_, i) => <col key={i} className="jt-time" />)}
                </colgroup>
                <thead>
                  <tr><th>Job</th><th>Outcome</th><th>On route</th><th>Arrived</th><th>Work started</th><th>Finished</th><th>Travel</th><th>Setting up</th><th>Working</th><th>Paused</th></tr>
                </thead>
                <tbody>
                  {d.jobs.map((j) => {
                    const t = j.timing;
                    return (
                      <tr key={j.id}>
                        <td data-label="Job"><Link to={`/bookings/list?booking=${j.id}`}><strong>{j.reference}</strong></Link><div className="cell-sub">{j.address}, {j.postcode}</div></td>
                        <td data-label="Outcome">{STATUS_LABEL[j.status] || j.status}{j.status !== 'completed' && j.statusReason && <div className="cell-sub">{j.statusReason}</div>}</td>
                        <td data-label="On route">{clock(t.onRouteAt)}</td>
                        <td data-label="Arrived">{clock(t.arrivedAt)}</td>
                        <td data-label="Work started">{t.workStartedAt ? clock(t.workStartedAt) : <span className="muted" title="No risk assessment page 2 photo">–</span>}</td>
                        <td data-label="Finished">{t.finished ? clock(t.finishedAt) : <span className="muted">not yet</span>}</td>
                        <td data-label="Travel">
                          {/* Time at a storeroom (collecting stock) isn't held against the travel. */}
                          <Flag mins={t.travelMins} over={t.travelMins == null ? null : LOOK.travelMins + (t.storeroomMins || 0)} title="Over an hour from On route to Arrived, not counting time at a storeroom" />
                          {t.storeroomMins > 0 && <div className="cell-sub" title={t.storeroomStops.map((v) => `${v.storeroom} ${clock(v.arrivedAt)} to ${v.leftAt ? clock(v.leftAt) : 'now'}`).join('\n')}>incl. {hm(t.storeroomMins)} at the storeroom</div>}
                        </td>
                        <td data-label="Setting up"><Flag mins={t.setupMins} over={LOOK.setupMins} title="Over half an hour from arriving to the risk assessment page 2" /></td>
                        <td data-label="Working">{hm(t.workMins)}</td>
                        <td data-label="Paused">{t.pausedMins ? hm(t.pausedMins) : '–'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}
